import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAnnouncementContext } from "../../context/AnnouncementContext";
import { useToastContext } from "../../context/ToastContext";
import { userService } from "../../services/UserService";
import { useLoader } from "../../context/LoaderContext";

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { showAnnouncement } = useAnnouncementContext();
  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();
  const inviteToken = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );

  useEffect(() => {
    const acceptInvite = async () => {
      if (!inviteToken) {
        showToast(
          "error",
          "Oops! We couldn’t find your invitation. Make sure to use the link provided in your email."
        );
        setStatus("error");
        return;
      }

      showLoader();

      try {
        const response = await userService.acceptInvitation(inviteToken);

        showAnnouncement(
          "success",
          response.message || "Invitation accepted successfully."
        );
        setStatus("success");
        hideLoader();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "Failed to accept the invitation.";

        showToast("error", errorMessage);
        setStatus("error");
        hideLoader();
      }
    };

    acceptInvite();
  }, [inviteToken, isLoggedIn, showAnnouncement, showToast]);

  const handleRetry = () => {
    setStatus("loading");
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleContinue = () => {
    if (isLoggedIn) {
      navigate("/user-management");
    } else {
      navigate("/auth");
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-background-default dark:bg-background-dark px-6">
      {status === "loading" && (
        <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark">
          <h2 className="text-2xl font-semibold mt-6">
            Accepting Invitation...
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-2">
            Hang on, this process only takes a moment.
          </p>
        </div>
      )}

      {status === "success" && (
        <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark">
          <h2 className="text-2xl font-semibold">Invitation Accepted!</h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-4">
            You can now access your organization.
          </p>
          <button
            onClick={handleContinue}
            className="mt-6 px-6 py-3 border-2 border-green-dark text-green-dark rounded-lg font-bold text-sm hover:bg-green-dark hover:text-white transition"
          >
            {isLoggedIn ? "Go to Dashboard" : "Login to Continue"}
          </button>
        </div>
      )}

      {status === "error" && (
        <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark">
          <h2 className="text-2xl font-semibold">Invitation Failed</h2>
          <p className="text-sm text-center text-text-secondary dark:text-text-secondaryDark mt-4">
            We're unable to process your invitation right now. Please reach out
            <br />
            to the sender or our support team for assistance.
          </p>
          <button
            onClick={handleRetry}
            className="mt-6 px-6 py-3 border-2 border-red-dark text-red-dark rounded-lg font-bold text-sm hover:bg-red-dark hover:text-white transition flex items-center justify-center"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default AcceptInvitation;
